import { CSSProperties, useMemo, useState } from "react";
import Form from "react-bootstrap/Form";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import classNames from "classnames";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { IdwFormLabelWithIconContainer } from "../idw-form-label-with-icon-container/idw-form-label-with-icon-container";
import { ColorPickerContainer } from "../color-picker-container/color-picker-container";
import styles from "./color-picker-form-group.less";

type ColorPickerFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId?: string;
    ariaLabel?: string;
    placeholder?: string;
    label: string;
    value: string;
    type?: string;
    onChange: (newValue: string) => void;
    layout?: LayoutTypes;
    disabled: boolean;
    isRequired: boolean;
    position?: "top" | "bottom";
    afterLabelTooltipText?: string;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    rightAlignedLabelElement?: React.ReactNode;
};

export const ColorPickerFormGroup = (props: ColorPickerFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        placeholder,
        label,
        value,
        type = "text",
        onChange,
        layout,
        disabled,
        isRequired,
        position = "top",
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterLabelTooltipText,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        rightAlignedLabelElement
    } = props;

    const [showColorPicker, setShowColorPicker] = useState(false);

    const formGroupClassName = useMemo(() => {
        return layout === LayoutTypes.COLUMN ? styles.colorPickerFormGroupColumn : styles.colorPickerFormGroupRow;
    }, [layout]);

    const onClickColorPickerInput = () => {
        setShowColorPicker(true);
    };

    const onClickCancel = () => {
        setShowColorPicker(false);
    };

    const onClickSave = (newValue: string) => {
        onChange(newValue);
        setShowColorPicker(false);
    };

    const colorSwatchStyle: CSSProperties = {
        backgroundColor: value
    };

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
            <IdwFormLabelWithIconContainer
                controlId={controlId}
                labelWidth={labelWidth}
                layout={layout}
                label={label}
                isRequired={isRequired}
                afterLabelTooltipText={afterLabelTooltipText}
                afterLabelIcon={afterLabelIcon}
                afterLabelIconDisabled={afterLabelIconDisabled}
                afterLabelIconOnClick={afterLabelIconOnClick}
                rightAlignedLabelElement={rightAlignedLabelElement}
            />
            <Form.Control
                as={"input"}
                className={styles.colorPickerInput}
                automation-id={automationId}
                aria-label={ariaLabel}
                placeholder={placeholder}
                type={type}
                value={value}
                disabled={disabled}
                readOnly={!disabled}
                onClick={onClickColorPickerInput}
            />
            <div
                id={`${controlId}-select-color`}
                className={classNames(styles.colorSwatch, disabled ? styles.disabledColorSwatch : "")}
                style={colorSwatchStyle}
                onClick={disabled ? undefined : onClickColorPickerInput}
            />
            {showColorPicker && (
                <ColorPickerContainer
                    controlId={controlId}
                    initialColor={value}
                    position={position}
                    onClickSave={onClickSave}
                    onClickCancel={onClickCancel}
                />
            )}
            {afterInputIcon && afterInputIconOnClick && (
                <div>
                    <ClickableIcon controlId={controlId} disabled={afterInputIconDisabled} icon={afterInputIcon} onClick={afterInputIconOnClick} className={styles.afterInputIcon} />
                </div>
            )}
        </Form.Group>
    );
};